/**
 * Created by katarinababic on 28.3.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { PropsWithChildren } from 'react';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const BlackBox = styled.div`
  width: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 1.5rem;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  @media (min-width: 390px) {
    padding: 2rem;
  }

  @media (min-width: 768px) {
    align-self: center;
    border-radius: 24px;
    padding: 3rem 2.5rem 2.5rem 2.5rem;
  }

  @media (min-width: 1280px) {
    max-width: 90%;
  }

  @media (min-width: 1600px) {
    max-width: 80%;
  }

  @media (min-width: 1768px) {
    max-width: 70%;
  }

  @media (min-width: 2300px) {
    max-width: 50%;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 1.5rem;
  animation: ${fadeInUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    display: none;
  }
`;

const LogoWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    background: linear-gradient(135deg, rgba(0, 221, 163, 0.3), rgba(0, 175, 150, 0.3));
    border-radius: 50%;
    filter: blur(12px);
    z-index: -1;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const logoStyle: React.CSSProperties = {
  width: '4rem',
  height: '4rem',
  borderRadius: '12px',
};

const TextContainer = styled(Container)`
  padding: 0 1rem 0 1rem;
`;

const Title = styled.h5`
  color: #ffffff;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 2.25rem;
  margin-bottom: 0;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both;
`;

const TitleUnderline = styled.div`
  background: linear-gradient(90deg, #00dda2, #00AF96, #00dda2);
  background-size: 200% 100%;
  height: 4px;
  border-radius: 2px;
  margin: 12px 0 16px 0;
  width: 80px;
  animation: ${shimmer} 3s ease-in-out infinite, ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both;
`;

const Subtitle = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.7rem;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both;
  
  strong {
    color: #00dda2;
    font-weight: 700;
  }
`;

const Text = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.5rem;
  padding-top: 0.5rem;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.25s both;
`;

export type AuthLayoutBlackBoxProps = {
  title: string;
  subtitle?: string | null;
  text?: string | null;
};

export const AuthLayoutBlackBox: React.FC<PropsWithChildren<AuthLayoutBlackBoxProps>> = observer(
  ({ title, subtitle, text, children }) => {
    return (
      <BlackBox>
        <LogoContainer>
          <LogoWrapper>
            <img alt="logo" src={require('../../../Assets/Images/logo.png')} style={logoStyle} />
          </LogoWrapper>
        </LogoContainer>
        <TextContainer>
          <Title>{title}</Title>
          <TitleUnderline />
          {!!subtitle && (
            <Subtitle>
              <MarkdownRenderer text={subtitle} />
            </Subtitle>
          )}
          {!!text && <Text>{text}</Text>}
        </TextContainer>
        {children}
      </BlackBox>
    );
  },
);
